/**
 * FlowChart
 *
 * A component for creating interactive flow charts in chat messages
 *
 * Primary responsibilities:
 * - Visualize process flows and decision trees
 * - Support for multiple node types (process, decision, start/end)
 * - Allow for custom node styling and data from AI
 * - Provide clear visual representation of flows
 */

import React from 'react'
import { cn } from '@/lib/utils'
import {
  IconCircle,
  IconDiamond,
  IconSquareRounded,
  IconSquareRoundedArrowRight,
  IconHexagon
} from '@tabler/icons-react'

export interface FlowNode {
  id: string
  text: string
  type: 'start' | 'end' | 'process' | 'decision' | 'input' | 'output' | string
  next?: string[] // IDs of next nodes in flow
  position?: { x: number; y: number }
  style?: string // Custom styling class
  description?: string
  notes?: string
  [key: string]: any // Allow for additional custom properties
}

interface FlowChartProps {
  title?: string
  nodes: FlowNode[]
  className?: string
  layout?: 'vertical' | 'horizontal' | 'auto'
  showLabels?: boolean
  compact?: boolean
}

export const FlowChart: React.FC<FlowChartProps> = ({
  title = 'Flow Chart',
  nodes,
  className,
  layout = 'vertical',
  showLabels = true,
  compact = false
}) => {
  // Build connectivity map
  const getNextNodes = (nodeId: string): FlowNode[] => {
    const node = nodes.find((n) => n.id === nodeId)
    if (!node || !node.next || node.next.length === 0) return []

    return node.next
      .map((nextId) => nodes.find((n) => n.id === nextId))
      .filter(Boolean) as FlowNode[]
  }

  // Find start nodes (nodes that are not referenced as 'next' in any other node)
  const getStartNodes = (): FlowNode[] => {
    // Get all nodes that are referenced as 'next'
    const nextNodeIds = new Set<string>()
    nodes.forEach((node) => {
      if (node.next) {
        node.next.forEach((nextId) => nextNodeIds.add(nextId))
      }
    })

    // Find nodes that are explicitly marked as start or not referenced as 'next'
    const startNodes = nodes.filter((node) => node.type === 'start' || !nextNodeIds.has(node.id))

    return startNodes.length > 0 ? startNodes : [nodes[0]] // Fallback to first node if no starts
  }

  const getNodeIcon = (node: FlowNode) => {
    // If node has custom icon, use that
    if (node.icon) return node.icon

    switch (node.type) {
      case 'start':
        return <IconCircle size={16} className="text-green-500" />
      case 'end':
        return <IconCircle size={16} className="text-red-500" />
      case 'process':
        return <IconSquareRounded size={16} className="text-blue-500" />
      case 'decision':
        return <IconDiamond size={16} className="text-amber-500" />
      case 'input':
        return <IconSquareRoundedArrowRight size={16} className="text-purple-500" />
      case 'output':
        return <IconHexagon size={16} className="text-teal-500" />
      default:
        // Support custom node types
        return <IconSquareRounded size={16} className="text-gray-500" />
    }
  }

  const getNodeColorClass = (node: FlowNode) => {
    // If node has custom style, use that
    if (node.style) return node.style

    // Otherwise use type-based styles
    switch (node.type) {
      case 'start':
        return 'border-green-400 bg-green-50 dark:bg-green-900/20'
      case 'end':
        return 'border-red-400 bg-red-50 dark:bg-red-900/20'
      case 'process':
        return 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
      case 'decision':
        return 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
      case 'input':
        return 'border-purple-400 bg-purple-50 dark:bg-purple-900/20'
      case 'output':
        return 'border-teal-400 bg-teal-50 dark:bg-teal-900/20'
      default:
        // Support custom types with fallback styling
        return 'border-gray-400 bg-gray-50 dark:bg-gray-900/20'
    }
  }

  const getConnectorClass = (node: FlowNode) => {
    switch (node.type) {
      case 'decision':
        return 'border-amber-300 dark:border-amber-700'
      default:
        return 'border-blue-300 dark:border-blue-700'
    }
  }

  const renderNode = (node: FlowNode, level: number = 0) => {
    const nextNodes = getNextNodes(node.id)
    const isDecision = node.type === 'decision'
    const showDetails = !compact && (node.description || node.notes)

    return (
      <div key={node.id} className="flow-node">
        <div
          className={cn(
            'flex items-start gap-2 rounded-md border-2 p-3 transition-colors',
            getNodeColorClass(node),
            isDecision ? 'min-w-[200px]' : '',
            compact ? 'py-2' : ''
          )}
        >
          <div className="mt-0.5">{getNodeIcon(node)}</div>

          <div className="flex-1">
            <div className="flex flex-col">
              {showLabels && (
                <span className="text-muted-foreground text-xs uppercase tracking-wider">
                  {node.type}
                </span>
              )}
              <span className={cn('font-medium', compact ? 'text-xs' : 'text-sm')}>
                {node.text}
              </span>

              {showDetails && node.description && (
                <p className="text-muted-foreground mt-1 text-xs">{node.description}</p>
              )}

              {showDetails && node.notes && (
                <div className="mt-1 text-xs">
                  <span className="font-medium">Note: </span>
                  <span className="text-muted-foreground">{node.notes}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {nextNodes.length > 0 && (
          <div
            className={cn(
              layout === 'horizontal' ? 'mt-6 flex items-start gap-8' : 'mt-2',
              isDecision && layout !== 'horizontal' ? 'grid grid-cols-2 gap-4' : ''
            )}
          >
            {nextNodes.map((nextNode, index) => (
              <div
                key={nextNode.id}
                className={cn(
                  layout === 'horizontal'
                    ? 'relative flex-1'
                    : cn(
                        'relative pl-8 pt-6',
                        compact ? 'pl-6 pt-4' : '',
                        isDecision && index === 0 ? 'border-r-0' : '',
                        isDecision && index === 1 ? 'border-l-0' : ''
                      )
                )}
              >
                {layout !== 'horizontal' && (
                  <div
                    className={cn(
                      'absolute left-0 top-0 h-full w-6 border-b-2 border-l-2',
                      getConnectorClass(node),
                      isDecision && index === 0 ? 'rounded-bl-lg' : '',
                      isDecision && index === 1 ? 'left-auto right-full rounded-br-lg' : '',
                      compact ? 'w-4' : ''
                    )}
                    style={{
                      height: isDecision ? '16px' : '100%'
                    }}
                  />
                )}

                {layout === 'horizontal' && (
                  <div className="absolute left-0 top-0 flex h-6 w-full items-center justify-center">
                    <div className="h-0.5 w-8 bg-blue-300 dark:bg-blue-700" />
                    <div className="size-3 rounded-full bg-blue-300 dark:bg-blue-700" />
                  </div>
                )}

                {isDecision && (
                  <div className="text-muted-foreground mb-1 text-xs">
                    {index === 0 ? 'Yes' : 'No'}
                  </div>
                )}

                {renderNode(nextNode, level + 1)}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="border-b p-3">
        <div className="flex items-center gap-2">
          <IconSquareRounded size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
        </div>
      </div>

      <div className="p-4">{getStartNodes().map((startNode) => renderNode(startNode))}</div>
    </div>
  )
}
